import React, { useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  FiChevronLeft,
  FiFileText,
  FiShield,
  FiHelpCircle,
  FiMail,
  FiMessageSquare,
} from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  icon: 'file' | 'shield' | 'users' | 'help';
  children: React.ReactNode;
  showContact?: boolean;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  lastUpdated,
  icon,
  children,
  showContact = true,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const IconComponent = {
    file: FiFileText,
    shield: FiShield,
    users: FiUsers,
    help: FiHelpCircle,
  }[icon];

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <nav className="flex items-center gap-2 text-zinc-500 text-[12px] font-medium tracking-wide">
            <Link to="/profile/settings" className="hover:text-white transition-colors">
              Settings
            </Link>
            <span className="text-zinc-800 font-light">/</span>
            <span className="text-zinc-200">Legal</span>
          </nav>

          <button
            onClick={() => navigate({ to: '/profile/settings' })}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-all group w-fit"
          >
            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
              <FiChevronLeft size={16} />
            </div>
            <span className="text-sm font-medium">Back to Settings</span>
          </button>
        </div>

        <div className="mb-12 relative">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-500/5 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-500/5 blur-[120px] rounded-full pointer-events-none" />

          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center shadow-lg">
              <IconComponent size={28} className="text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">{title}</h1>
              <p className="text-zinc-500 text-sm mt-1">
                Last Updated: <span className="text-zinc-300 font-medium">{lastUpdated}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">{children}</div>

        {showContact && (
          <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm">
            <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <FiMessageSquare size={20} className="text-blue-400" />
              </div>
              Need Help?
            </h3>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:support@musicade.com"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition-all hover:scale-105"
              >
                <FiMail size={18} />
                Contact Support
              </a>
            </div>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
